import type { JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoginScreen, type LoginResult, type LoginScreenEndpoints } from '@cca/ui';

// Corpus (Liturgy Portal) adopts the estate's ONE sign-in screen (ADR-018 /
// AUTH-8): the shared @cca/ui <LoginScreen> replaces corpus's hand-built
// LoginPage + VerifyPage clones. Plum #7E255C is corpus's signature colour — the
// SAME accent the worker hands renderOtpCodeEmail, so the screen and the
// login-code email are two faces of one door (UX-1). Cookie session model: the
// server's Set-Cookie does the work, so onSignedIn just enters the app (no token
// to persist). The design-system classes (@cca/ui/components) + role tokens
// (@cca/ui/styles) LoginScreen paints with are already loaded globally in
// main.tsx, so nothing extra is imported here (matches the forms AdminSignIn
// reference and avoids the double-theme trap).

/** Corpus's same-origin worker paths. Passkey bases are unchanged; OTP now mounts
 *  the shared createOtpSignInRoutes factory, so its two paths are /otp/request +
 *  /otp/verify (were /otp-request + /otp-verify). */
const CORPUS_ENDPOINTS: LoginScreenEndpoints = {
  discoverOptions: '/api/auth/passkeys/discover-options',
  discoverVerify: '/api/auth/passkeys/discover-verify',
  passkeyOptions: '/api/auth/passkeys/options',
  passkeyVerify: '/api/auth/passkeys/verify',
  otpRequest: '/api/auth/otp/request',
  otpVerify: '/api/auth/otp/verify',
};

/** The unauthenticated sign-in door. Mounted at /auth/login; the shared screen
 *  owns BOTH the email step and the code step internally, so corpus no longer
 *  needs a separate routed /auth/verify. */
export function CorpusSignIn(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/';

  const onSignedIn = (result: LoginResult): void => {
    // The session cookie is set by the server (HttpOnly); keep only the display
    // hint the old flow persisted, then enter the app.
    if (result.email) localStorage.setItem('codex_email', result.email);
    navigate(returnTo, { replace: true });
  };

  return (
    <LoginScreen
      portalName="Liturgy Portal"
      accentColor="#7E255C"
      endpoints={CORPUS_ENDPOINTS}
      onSignedIn={onSignedIn}
      authzDeniedCopy="You do not have access to this portal."
    />
  );
}
